import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileDown } from "lucide-react";

export default function ModelModal({ model, onClose, onBook }) {
  const [brochureOpen, setBrochureOpen] = useState(false);
  const [brochurePhone, setBrochurePhone] = useState("");
  const [brochureBusy, setBrochureBusy] = useState(false);

  const downloadBrochure = async () => {
    if (!/^[0-9+\s-]{10,15}$/.test(brochurePhone.trim())) {
      toast.error("Enter a valid mobile number");
      return;
    }
    setBrochureBusy(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/brochure`, {
        model: model.id,
        phone: brochurePhone.trim(),
      });
      window.open(data.url, "_blank", "noopener");
      toast.success("Brochure opening in a new tab");
      setBrochureOpen(false);
      setBrochurePhone("");
    } catch {
      toast.error("Could not get the brochure right now");
    } finally {
      setBrochureBusy(false);
    }
  };
  return (
    <AnimatePresence>
      {model && (
        <motion.div
          data-testid="model-specs-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="model-modal-backdrop" />
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-3xl bg-[#0D1822] border border-white/10 clip-card overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            <div className="relative h-56 sm:h-72 card-spotlight">
              <img
                src={model.image}
                alt={model.name}
                className={`w-full h-full ${model.fit === "cover" ? "object-cover" : "object-contain p-6 sm:p-10"}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D1822] via-[#0D1822]/30 to-transparent" />
              <button
                data-testid="model-modal-close-button"
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 grid place-items-center bg-black/50 backdrop-blur-md border border-white/15 text-white hover:border-[#FFFFFF] transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <div className="absolute bottom-5 left-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFFFFF]">{model.badge}</p>
                <h3 className="font-display font-extrabold text-3xl sm:text-4xl text-white tracking-tight mt-1">{model.name}</h3>
                <p className="text-slate-400 italic text-sm mt-1">{model.tagline}</p>
              </div>
            </div>
            <div className="p-6 sm:p-8">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
                {Object.entries(model.specs).map(([k, v]) => (
                  <div key={k} data-testid={`model-spec-${k.toLowerCase()}`} className="border-l-2 border-[#FFFFFF]/60 pl-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{k}</p>
                    <p className="text-sm text-slate-200 mt-1 leading-relaxed">{v}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Starting at</p>
                  <p data-testid="model-modal-price" className="font-display font-extrabold text-3xl text-[#FFFFFF] mt-1">{model.price}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    data-testid="model-modal-book-button"
                    onClick={() => onBook(model)}
                    className="bg-[#FFFFFF] hover:bg-[#D4D4D4] text-black text-xs font-semibold uppercase tracking-[0.18em] px-8 py-4 clip-notch transition-colors duration-300"
                  >
                    Book a Test Drive
                  </button>
                  <button
                    data-testid="model-modal-brochure-button"
                    onClick={() => setBrochureOpen((v) => !v)}
                    className="inline-flex items-center gap-2 border border-white/20 hover:border-[#FFFFFF] text-white text-xs font-semibold uppercase tracking-[0.18em] px-6 py-4 transition-colors duration-300"
                  >
                    <FileDown size={14} /> Brochure
                  </button>
                </div>
              </div>
              {brochureOpen && (
                <div data-testid="brochure-gate" className="mt-5 flex flex-wrap items-center gap-3">
                  <input
                    data-testid="brochure-phone-input"
                    type="tel"
                    value={brochurePhone}
                    onChange={(e) => setBrochurePhone(e.target.value)}
                    placeholder="Your mobile number"
                    className="kia-input flex-1 min-w-[200px]"
                  />
                  <button
                    data-testid="brochure-download-button"
                    onClick={downloadBrochure}
                    disabled={brochureBusy}
                    className="inline-flex items-center gap-2 bg-[#FFFFFF] hover:bg-[#D4D4D4] disabled:opacity-40 text-black text-xs font-semibold uppercase tracking-[0.15em] px-6 py-3.5 clip-notch transition-colors"
                  >
                    {brochureBusy ? "Opening..." : "Get Official PDF"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
